/**
 * Admin "+ New arc" page (Refinement Phase A2).
 *
 * Five-field form against POST /v2/staging/arcs:
 *   • slug             — non-empty, [a-z0-9][a-z0-9-]{0,119}
 *   • title            — trimmed non-empty, ≤ 500 chars
 *   • arc_type         — closed allowlist: season | special | spinoff
 *   • status           — closed allowlist: scheduled | active | completed
 *   • idempotency_key  — crypto.randomUUID() per form open, reused across retries
 *
 * Locked scope (per FISHSTANK_V2_ARC_CREATE_FIRST_SLICE.md):
 *   - No display_order / start_date / end_date / summary_md /
 *     winner_entity_id editing — those default to NULL on the new
 *     row and land in A3 / future phases.
 *   - No edit / delete / unmerge surface from this page.
 *
 * Idempotency: one crypto.randomUUID() per form open, reused across
 * retries. A double-click replays on
 * mutation_events.idempotency_key UNIQUE without writing a second
 * audit row.
 *
 * On success: surface a small receipt banner with a link back to
 * `/arcs` (where the new row is now visible) and a link to "Edit
 * summary" via the existing CP10 surface.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import type {
  ArcCreateRequest,
  ArcMutationReceipt,
  ArcRowSnapshot,
  ArcStatusEditable,
  ArcTypeEditable,
  ArcValidationError,
} from '@fishstank/contracts'
import {
  ARC_STATUSES_EDITABLE,
  ARC_TYPES_EDITABLE,
} from '@fishstank/contracts'

import { Shell } from '../layouts/Shell'
import { type ArcMutationFetchResult, postArc } from '../lib/worker'

function newIdempotencyKey(): string {
  return globalThis.crypto.randomUUID()
}

function fieldFromError(err: ArcValidationError): string | null {
  switch (err.code) {
    case 'invalid_body':
      return null
    case 'slug_required':
    case 'slug_too_long':
    case 'invalid_slug':
    case 'slug_conflict':
      return 'slug'
    case 'title_required':
    case 'title_too_long':
      return 'title'
    case 'invalid_enum':
      return err.field
    case 'invalid_uuid':
    case 'idempotency_key_in_use':
      return 'idempotency_key'
    case 'another_arc_active':
      return 'status'
    default:
      return null
  }
}

function validationMessage(err: ArcValidationError): string {
  switch (err.code) {
    case 'invalid_body':
      return 'Request body must be a JSON object.'
    case 'slug_required':
      return 'Slug is required.'
    case 'slug_too_long':
      return `Slug is too long (max ${err.max} characters).`
    case 'invalid_slug':
      return 'Slug must be lowercase letters, digits, and hyphens; must start with a letter or digit.'
    case 'title_required':
      return 'Title is required.'
    case 'title_too_long':
      return `Title is too long (max ${err.max} characters).`
    case 'invalid_enum':
      return `"${err.field}" is not allowed (expected one of: ${err.allowed.join(', ')}).`
    case 'invalid_uuid':
      return 'Internal error: idempotency key must be a UUID. Refresh and try again.'
    case 'slug_conflict':
      return 'Another arc already uses this slug.'
    case 'another_arc_active':
      return 'Another arc is already marked active. Set this one to "scheduled" or "completed", or end the active arc first.'
    case 'idempotency_key_in_use':
      return 'This form was already submitted for a different request. Refresh to start a new arc.'
    default:
      return 'Validation failed.'
  }
}

export function ArcCreatePage(): JSX.Element {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Pinned per form-open. Stable across renders so a re-render or
  // React-Query cache invalidation doesn't accidentally flip the key.
  const idempotencyKey = useMemo(() => newIdempotencyKey(), [])

  const [slug, setSlug] = useState<string>('')
  const [title, setTitle] = useState<string>('')
  const [arcType, setArcType] = useState<ArcTypeEditable>('season')
  const [status, setStatus] = useState<ArcStatusEditable>('scheduled')

  const [validationError, setValidationError] =
    useState<ArcValidationError | null>(null)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<ArcRowSnapshot | null>(null)

  const mutation = useMutation<
    ArcMutationFetchResult<ArcMutationReceipt>,
    unknown,
    ArcCreateRequest
  >({
    mutationFn: (body) => postArc({ body }),
    onSuccess: (res) => {
      setValidationError(null)
      setNetworkError(null)
      if (res.kind === 'ok') {
        setReceipt(res.artifact.body.row)
        // Refresh the /arcs list cache so the new row shows up next visit.
        queryClient.invalidateQueries({ queryKey: ['arcs', 'admin', 'list'] })
      } else if (res.kind === 'validation_error') {
        setValidationError(res.error)
      } else if (res.kind === 'csrf_error') {
        setNetworkError(`CSRF: ${res.reason}`)
      } else if (res.kind === 'not_found') {
        // A2 create cannot reach this branch in practice (the create
        // route has no `:id` to mis-resolve), but the
        // ArcMutationFetchResult union now carries the variant for
        // A3's edit path. Surface it as a generic network message.
        setNetworkError(`Not found (HTTP ${res.status})`)
      } else {
        setNetworkError(res.message)
      }
    },
  })

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setValidationError(null)
    setNetworkError(null)
    const body: ArcCreateRequest = {
      slug: slug.trim(),
      title: title.trim(),
      arc_type: arcType,
      status,
      idempotency_key: idempotencyKey,
    }
    mutation.mutate(body)
  }

  const errorField = validationError ? fieldFromError(validationError) : null

  return (
    <Shell>
      <header className="mb-6">
        <h1 className="text-2xl font-bold">New arc</h1>
        <p className="mt-1 text-sm text-slate-400">
          <Link to="/arcs" className="text-fishstank-accent underline-offset-2 hover:underline">
            ← Back to arcs
          </Link>
        </p>
      </header>

      <form
        data-testid="arc-create-form"
        className="space-y-6"
        onSubmit={handleSubmit}
      >
        <div>
          <label
            htmlFor="arc-slug"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Slug
          </label>
          <input
            id="arc-slug"
            data-testid="arc-create-slug"
            type="text"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 font-mono text-slate-100"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            disabled={mutation.isPending}
            pattern="[a-z0-9][-a-z0-9]*"
            maxLength={120}
            required
          />
          <p className="mt-1 text-xs text-slate-500">
            Lowercase letters, digits, and hyphens; must start with a letter or digit.
          </p>
          {errorField === 'slug' && validationError && (
            <p
              data-testid="arc-create-error-slug"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {validationMessage(validationError)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="arc-title"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Title
          </label>
          <input
            id="arc-title"
            data-testid="arc-create-title"
            type="text"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={mutation.isPending}
            maxLength={500}
            required
          />
          {errorField === 'title' && validationError && (
            <p
              data-testid="arc-create-error-title"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {validationMessage(validationError)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="arc-type"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Type
          </label>
          <select
            id="arc-type"
            data-testid="arc-create-arc-type"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={arcType}
            onChange={(e) => setArcType(e.target.value as ArcTypeEditable)}
            disabled={mutation.isPending}
            required
          >
            {ARC_TYPES_EDITABLE.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {errorField === 'arc_type' && validationError && (
            <p
              data-testid="arc-create-error-arc-type"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {validationMessage(validationError)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="arc-status"
            className="mb-1 block text-sm font-medium text-slate-200"
          >
            Status
          </label>
          <select
            id="arc-status"
            data-testid="arc-create-status"
            className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-slate-100"
            value={status}
            onChange={(e) => setStatus(e.target.value as ArcStatusEditable)}
            disabled={mutation.isPending}
            required
          >
            {ARC_STATUSES_EDITABLE.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-slate-500">
            "cancelled" is reserved for a future flow and not editable here.
          </p>
          {errorField === 'status' && validationError && (
            <p
              data-testid="arc-create-error-status"
              data-error-code={validationError.code}
              className="mt-1 text-sm text-rose-300"
            >
              {validationMessage(validationError)}
            </p>
          )}
        </div>

        {validationError && errorField === null && (
          <div
            data-testid="arc-create-validation-error"
            data-error-code={validationError.code}
            className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
          >
            <strong>{validationError.code}</strong>
            <span className="ml-2">{validationMessage(validationError)}</span>
          </div>
        )}

        {networkError && (
          <div
            data-testid="arc-create-network-error"
            className="rounded border border-rose-700 bg-rose-950/40 p-4 text-rose-100"
          >
            {networkError}
          </div>
        )}

        {receipt && (
          <div
            data-testid="arc-create-receipt"
            className="rounded border border-emerald-700 bg-emerald-950/40 p-4 text-emerald-100"
          >
            Created arc{' '}
            <strong data-testid="arc-create-receipt-title">{receipt.title}</strong>{' '}
            (slug <code data-testid="arc-create-receipt-slug">{receipt.slug}</code>,
            type <code data-testid="arc-create-receipt-arc-type">{receipt.arc_type}</code>,
            status <code data-testid="arc-create-receipt-status">{receipt.status}</code>).{' '}
            <Link
              to="/arcs"
              data-testid="arc-create-back-to-arcs"
              className="underline underline-offset-2"
            >
              Back to arcs
            </Link>
            {' · '}
            <Link
              to={`/wiki/arcs/${receipt.id}/edit`}
              data-testid={`arc-create-edit-summary-link-${receipt.id}`}
              className="underline underline-offset-2"
            >
              Edit summary
            </Link>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            data-testid="arc-create-submit"
            className="rounded bg-fishstank-accent px-4 py-2 text-sm font-semibold text-slate-900 disabled:opacity-50"
            disabled={mutation.isPending || receipt !== null}
          >
            {mutation.isPending ? 'Creating…' : 'Create arc'}
          </button>
          <button
            type="button"
            data-testid="arc-create-cancel"
            className="rounded border border-slate-700 px-4 py-2 text-sm text-slate-200"
            onClick={() => navigate('/arcs')}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
        </div>
      </form>
    </Shell>
  )
}
